import { Controller, FormProvider, useForm } from "react-hook-form";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";

import { PencilIcon, UserIcon } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";

import { UpdateProfileSchema, updateProfileSchema } from "@/features/account/schemas";
import { updateProfile } from "@/features/account/services/account";
import Error from "@/shared/components/Error";
import Success from "@/shared/components/Success";
import { QUERY_KEYS } from "@/shared/lib/constants/queries";
import { User } from "@/shared/lib/types/user";
import { AppError } from "@/shared/lib/utils/errors";
import { getInitials } from "@/shared/lib/utils/user";

interface UpdateProfileFormProps {
  user: User;
}

export default function UpdateProfileForm({ user }: UpdateProfileFormProps) {
  const queryClient = useQueryClient();

  const form = useForm<UpdateProfileSchema>({
    resolver: zodResolver(updateProfileSchema),
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
    },
  });

  const watchedFirstName = form.watch("firstName");
  const watchedLastName = form.watch("lastName");

  const mutation = useMutation<Pick<User, "firstName" | "lastName">, AppError | Error, UpdateProfileSchema>({
    mutationFn: updateProfile,
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_DETAILS], exact: true });
      form.reset(updated);
    },
  });

  async function onSubmit(values: UpdateProfileSchema) {
    mutation.mutate(values);
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <UserIcon className="size-4 text-primary" />
          <CardTitle>Personal information</CardTitle>
        </div>
        <CardDescription>Your name is shown on tickets, comments and to other members of your projects.</CardDescription>
      </CardHeader>
      <CardContent>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            {mutation.isError && <Error title="Unable to update profile" error={mutation.error} />}
            {mutation.isSuccess && !form.formState.isDirty && <Success title="Profile updated" description="Your changes have been saved successfully." />}
            <FieldGroup>
              <div className="flex items-center gap-4 border border-border bg-muted/40 p-4">
                <Avatar size="lg">
                  <AvatarImage src="" alt={[watchedFirstName, watchedLastName].join(" ")} />
                  <AvatarFallback>{getInitials(watchedFirstName, watchedLastName)}</AvatarFallback>
                </Avatar>
                <div className="flex flex-col gap-0.5">
                  <p className="text-sm font-semibold">{[watchedFirstName, watchedLastName].filter(Boolean).join(" ") || "Your name"}</p>
                  <p className="text-xs text-muted-foreground">Your initials are used as your avatar across BugTracker.</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Controller
                  name="firstName"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor={field.name}>First name</FieldLabel>
                      <Input {...field} id={field.name} disabled={mutation.isPending} aria-invalid={fieldState.invalid} placeholder="e.g. Ada" autoComplete="given-name" />
                      <FieldDescription>Maximum 32 characters.</FieldDescription>
                      {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                    </Field>
                  )}
                />
                <Controller
                  name="lastName"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor={field.name}>Last name</FieldLabel>
                      <Input {...field} id={field.name} disabled={mutation.isPending} aria-invalid={fieldState.invalid} placeholder="e.g. Lovelace" autoComplete="family-name" />
                      <FieldDescription>Maximum 32 characters.</FieldDescription>
                      {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                    </Field>
                  )}
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button type="button" variant="outline" size="sm" disabled={mutation.isPending || !form.formState.isDirty} onClick={() => form.reset()}>
                  Cancel
                </Button>
                <Button size="sm" disabled={mutation.isPending || !form.formState.isDirty}>
                  <PencilIcon />
                  {mutation.isPending ? "Saving..." : "Save changes"}
                </Button>
              </div>
            </FieldGroup>
          </form>
        </FormProvider>
      </CardContent>
    </Card>
  );
}
